import { useCallback, useEffect, useRef, useState } from "react";
import {
    DraftContextMeshApiError,
    getDraftContextMeshAnalysis,
    scheduleDraftContextMeshAnalysis,
    type DraftCanonicalContextMeshAnalysis,
    type DraftContextMeshResponse,
    type DraftContextMeshStatus
} from "./draftContextMeshApi";

const ANALYSIS_DEBOUNCE_MS = 4_000;
const FAST_POLL_INTERVAL_MS = 2_000;
const SLOW_POLL_INTERVAL_MS = 10_000;
const FAST_POLL_ATTEMPTS = 15;

export type DraftContextMeshUiStatus = "unavailable" | "debouncing" | DraftContextMeshStatus;

export interface DraftContextMeshData {
    analysis: DraftCanonicalContextMeshAnalysis | null;
    analyzedDraftVersion: number | null;
    status: DraftContextMeshUiStatus;
    isStale: boolean;
    error: DraftContextMeshApiError | null;
    retry: () => void;
}

interface PersistedRevision {
    draftId: number;
    draftVersion: number;
}

interface UseDraftContextMeshOptions {
    enabled: boolean;
    token: string | null | undefined;
    draftId: number | null;
    revisionKey: string;
    baselineReady: boolean;
    ensurePersisted: (revisionKey: string, forceSave?: boolean) => Promise<PersistedRevision>;
    getAnalysis?: (token: string, draftId: number) => Promise<DraftContextMeshResponse>;
    scheduleAnalysis?: (token: string, draftId: number, draftVersion: number) => Promise<DraftContextMeshResponse>;
}

function shouldPoll(response: DraftContextMeshResponse): boolean {
    return response.status === "pending" || response.status === "sources_pending";
}

export function useDraftContextMeshAnalysis({
    enabled,
    token,
    draftId,
    revisionKey,
    baselineReady,
    ensurePersisted,
    getAnalysis = getDraftContextMeshAnalysis,
    scheduleAnalysis = scheduleDraftContextMeshAnalysis
}: UseDraftContextMeshOptions): DraftContextMeshData {
    const [analysis, setAnalysis] = useState<DraftCanonicalContextMeshAnalysis | null>(null);
    const [analyzedDraftVersion, setAnalyzedDraftVersion] = useState<number | null>(null);
    const [status, setStatus] = useState<DraftContextMeshUiStatus>(baselineReady ? "debouncing" : "baseline_incomplete");
    const [isStale, setIsStale] = useState(false);
    const [error, setError] = useState<DraftContextMeshApiError | null>(null);
    const [retryAttempt, setRetryAttempt] = useState(0);
    const retryImmediatelyRef = useRef(false);
    const displayedVersionRef = useRef<number | null>(null);
    const currentDraftVersionRef = useRef(-1);
    const currentReadyVersionRef = useRef<number | null>(null);
    const currentReadyGenerationRef = useRef<number | null>(null);
    const analysisRef = useRef<DraftCanonicalContextMeshAnalysis | null>(null);
    const requestGenerationRef = useRef(0);
    const restoreGenerationRef = useRef(0);
    const activeDraftIdRef = useRef<number | null>(draftId);

    const applyResponse = useCallback((response: DraftContextMeshResponse, responseGeneration: number) => {
        if (response.draftId !== activeDraftIdRef.current) return;
        if (response.currentDraftVersion < currentDraftVersionRef.current) return;
        const isExactCurrent = response.status === "ready"
            && response.resultSource === "current_ready"
            && response.analyzedDraftVersion === response.currentDraftVersion;
        const isActiveConfigRefresh = response.resultSource === "last_ready"
            && response.isStale
            && response.analyzedDraftVersion === response.currentDraftVersion;
        if (
            response.currentDraftVersion === currentDraftVersionRef.current &&
            currentReadyVersionRef.current === response.currentDraftVersion &&
            !isExactCurrent &&
            (!isActiveConfigRefresh || currentReadyGenerationRef.current === responseGeneration)
        ) return;
        currentDraftVersionRef.current = response.currentDraftVersion;
        if (isExactCurrent) {
            currentReadyVersionRef.current = response.currentDraftVersion;
            currentReadyGenerationRef.current = responseGeneration;
        }
        if (response.analysis && response.analyzedDraftVersion !== null) {
            const displayed = displayedVersionRef.current;
            if (displayed === null || response.analyzedDraftVersion >= displayed) {
                // The API parser admits an analysis only when the overall score and
                // all four canonical category scores form one valid bundle.
                displayedVersionRef.current = response.analyzedDraftVersion;
                analysisRef.current = response.analysis;
                setAnalysis(response.analysis);
                setAnalyzedDraftVersion(response.analyzedDraftVersion);
            }
        }
        setStatus(response.status);
        setIsStale(response.resultSource === "last_ready" || response.isStale);
        setError(response.status === "failed"
            ? new DraftContextMeshApiError(503, "ANALYSIS_FAILED", "Context analysis failed. Retry when you're ready.")
            : null);
    }, []);

    // Restore the last canonical result when a persisted draft is resumed.
    useEffect(() => {
        const previousDraftId = activeDraftIdRef.current;
        activeDraftIdRef.current = draftId;
        const generation = ++restoreGenerationRef.current;
        if (previousDraftId !== draftId) {
            currentDraftVersionRef.current = -1;
            currentReadyVersionRef.current = null;
            currentReadyGenerationRef.current = null;
            displayedVersionRef.current = null;
            analysisRef.current = null;
            setAnalysis(null);
            setAnalyzedDraftVersion(null);
            setIsStale(false);
        }
        if (!enabled || !token || draftId == null) {
            displayedVersionRef.current = null;
            analysisRef.current = null;
            setAnalysis(null);
            setAnalyzedDraftVersion(null);
            setIsStale(false);
            setError(null);
            setStatus("unavailable");
            return;
        }
        getAnalysis(token, draftId)
            .then(response => {
                if (restoreGenerationRef.current !== generation) return;
                applyResponse(response, -generation);
                // A resumed draft may have an analysis request in flight even
                // though its current required fields are incomplete. Preserve
                // any prior completed result, but never present that request as
                // active analysis until the baseline is valid again.
                if (!baselineReady) {
                    setStatus("baseline_incomplete");
                    setIsStale(response.analysis !== null || analysisRef.current !== null);
                    setError(null);
                }
            })
            .catch(reason => {
                if (restoreGenerationRef.current !== generation) return;
                // A missing first analysis is an ordinary state. The debounce
                // below will schedule one for the current persisted revision.
                if (reason instanceof DraftContextMeshApiError && reason.status === 404) return;
                setError(reason instanceof DraftContextMeshApiError
                    ? reason
                    : new DraftContextMeshApiError(503, "ANALYSIS_UNAVAILABLE", "Context analysis is temporarily unavailable."));
            });
    }, [applyResponse, baselineReady, draftId, enabled, getAnalysis, token]);

    useEffect(() => {
        if (!enabled) return;
        if (!baselineReady) {
            setStatus("baseline_incomplete");
            setIsStale(analysisRef.current !== null);
            return;
        }
        if (!token) {
            setStatus("unavailable");
            return;
        }
        const analysisToken = token;

        let cancelled = false;
        let pollTimer: ReturnType<typeof setTimeout> | null = null;
        let pollAttempts = 0;
        const generation = ++requestGenerationRef.current;
        setStatus("debouncing");
        setIsStale(analysisRef.current !== null);
        setError(null);

        const accept = (response: DraftContextMeshResponse) => {
            if (cancelled || requestGenerationRef.current !== generation) return false;
            applyResponse(response, generation);
            return true;
        };

        const scheduleNextPoll = (targetDraftId: number, targetVersion: number): void => {
            const interval = pollAttempts < FAST_POLL_ATTEMPTS ? FAST_POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS;
            pollAttempts += 1;
            pollTimer = setTimeout(() => poll(targetDraftId, targetVersion), interval);
        };

        function poll(targetDraftId: number, targetVersion: number): void {
            getAnalysis(analysisToken, targetDraftId)
                .then(response => {
                    if (!accept(response)) return;
                    const currentReady = response.status === "ready"
                        && response.resultSource === "current_ready"
                        && response.analyzedDraftVersion === targetVersion;
                    if (!currentReady && shouldPoll(response)) {
                        scheduleNextPoll(targetDraftId, targetVersion);
                    }
                })
                .catch(reason => {
                    if (cancelled || requestGenerationRef.current !== generation) return;
                    setStatus("failed");
                    setIsStale(analysisRef.current !== null);
                    setError(reason instanceof DraftContextMeshApiError
                        ? reason
                        : new DraftContextMeshApiError(503, "ANALYSIS_UNAVAILABLE", "Context analysis is temporarily unavailable."));
                });
        }

        const schedule = async () => {
            try {
                // Once a fresh schedule begins, a slow restore request must not
                // overwrite its newer status.
                restoreGenerationRef.current += 1;
                let persisted = await ensurePersisted(revisionKey);
                if (cancelled || requestGenerationRef.current !== generation) return;
                activeDraftIdRef.current = persisted.draftId;
                let response: DraftContextMeshResponse;
                try {
                    response = await scheduleAnalysis(analysisToken, persisted.draftId, persisted.draftVersion);
                } catch (reason) {
                    if (!(reason instanceof DraftContextMeshApiError) || reason.status !== 409) throw reason;
                    // A metadata-only autosave can advance the row version after
                    // the analysis key was captured. Resolve the newest persisted
                    // version once and resubmit without requiring user action.
                    const latest = await ensurePersisted(revisionKey, true);
                    if (cancelled || requestGenerationRef.current !== generation) return;
                    persisted = latest;
                    response = await scheduleAnalysis(analysisToken, latest.draftId, latest.draftVersion);
                }
                if (!accept(response)) return;
                if (shouldPoll(response)) scheduleNextPoll(persisted.draftId, persisted.draftVersion);
            } catch (reason) {
                if (cancelled || requestGenerationRef.current !== generation) return;
                setStatus("failed");
                setIsStale(analysisRef.current !== null);
                setError(reason instanceof DraftContextMeshApiError
                    ? reason
                    : new DraftContextMeshApiError(503, "ANALYSIS_UNAVAILABLE", reason instanceof Error ? reason.message : "Context analysis is temporarily unavailable."));
            }
        };

        const delay = retryImmediatelyRef.current ? 0 : ANALYSIS_DEBOUNCE_MS;
        retryImmediatelyRef.current = false;
        const debounceTimer = setTimeout(() => void schedule(), delay);
        return () => {
            cancelled = true;
            clearTimeout(debounceTimer);
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [applyResponse, baselineReady, enabled, ensurePersisted, getAnalysis, retryAttempt, revisionKey, scheduleAnalysis, token]);

    const retry = useCallback(() => {
        retryImmediatelyRef.current = true;
        setRetryAttempt(value => value + 1);
    }, []);

    return { analysis, analyzedDraftVersion, status, isStale, error, retry };
}
